import React, { useState, useRef } from 'react';
import { X, MessageSquare, Sparkles, Plus, Copy, Loader2, Info } from 'lucide-react';

interface Object3DInfo {
  id: string;
  name: string;
  type: string;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  material?: {
    color: string;
    roughness: number;
    metalness: number;
  };
}

interface Object3DAnnotatorProps {
  selectedObject?: Object3DInfo | null;
  onClose: () => void;
  onAnnotate: (text: string, objectInfo: Object3DInfo) => void;
  onAskAI: (objectInfo: Object3DInfo, notes: string) => void;
  onSendToChat: (message: string) => void;
  isGenerating?: boolean;
}

export default function Object3DAnnotator({
  selectedObject,
  onClose,
  onAnnotate,
  onAskAI,
  onSendToChat,
  isGenerating = false
}: Object3DAnnotatorProps) {
  const [annotationText, setAnnotationText] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);

  if (!selectedObject) return null;

  const infoString = JSON.stringify(selectedObject, null, 2);

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-[10%]"
      style={{ pointerEvents: 'none' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
        <div 
          ref={modalRef} 
          className="bg-[rgba(0,0,0,0.98)] border-2 border-white/20 shadow-2xl rounded-2xl w-full max-w-md"
          style={{ pointerEvents: 'auto', maxHeight: 'calc(100vh - 80px)' }}
        >
            {/* Header */}
            <div className="bg-[rgba(0,0,0,0.98)] border-b border-white/20 flex items-center justify-between px-4 h-10 rounded-t-2xl">
                <div className="flex items-center gap-2">
                    <Info size={14} className="text-white" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-white">Annotate 3D Object</span>
                </div>
                <button onClick={onClose} className="bg-black text-white hover:bg-white/10 p-1 rounded-md transition-colors"><X size={16} /></button>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[60vh]">
                <div className="bg-[rgba(0,0,0,0.98)] border border-white/20 rounded-lg p-3">
                     {/* Header: Type + ID */}
                     <div className="flex items-center justify-between border-b border-white/20 pb-2 mb-2">
                        <span className="font-terminal text-white font-bold text-sm">
                            <span className="opacity-50">&lt;</span>{selectedObject.type}<span className="opacity-50">&gt;</span>
                            <span className="text-white ml-1">#{selectedObject.id.slice(0, 8)}</span>
                        </span>
                     </div>
                     
                     {/* Transform Row */}
                     <div className="grid grid-cols-3 gap-2 mb-2 pb-2 border-b border-white/20 text-[8px]">
                        <div>
                            <span className="text-white">Position</span>
                            <div className="text-white font-mono">
                                {selectedObject.position.map(n => n.toFixed(1)).join(', ')}
                            </div>
                        </div>
                        <div>
                            <span className="text-white">Rotation</span>
                            <div className="text-white font-mono">
                                {selectedObject.rotation.map(n => n.toFixed(1)).join(', ')}
                            </div>
                        </div>
                        <div>
                            <span className="text-white">Scale</span>
                            <div className="text-white font-mono">
                                {selectedObject.scale.map(n => n.toFixed(1)).join(', ')}
                            </div>
                        </div>
                     </div>
                     
                     {/* Material Row */}
                     {selectedObject.material && (
                        <div className="grid grid-cols-3 gap-2 mb-2 pb-2 border-b border-white/20">
                            <div>
                                <span className="text-[8px] text-white uppercase">Color</span>
                                <div className="flex items-center gap-1 mt-0.5">
                                    <div className="w-3 h-3 rounded border border-white/20" style={{ backgroundColor: selectedObject.material.color }} />
                                    <span className="text-[8px] text-white font-mono truncate">{selectedObject.material.color}</span>
                                </div>
                            </div>
                            <div>
                                <span className="text-[8px] text-white uppercase">Roughness</span>
                                <div className="text-[9px] text-white font-mono mt-0.5">{selectedObject.material.roughness.toFixed(2)}</div>
                            </div>
                            <div>
                                <span className="text-[8px] text-white uppercase">Metalness</span>
                                <div className="text-[9px] text-white font-mono mt-0.5">{selectedObject.material.metalness.toFixed(2)}</div>
                            </div>
                        </div>
                     )}
                     
                     {/* Action Buttons: Copy & Send to Chat */}
                     <div className="flex gap-2 mt-3 pt-3 border-t border-white/20">
                        <button 
                          onClick={() => {
                            navigator.clipboard.writeText(infoString);
                          }}
                          className="flex-1 py-2 bg-[rgba(0,0,0,0.98)] border border-white/20 rounded-lg text-white text-[10px] font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 hover:border-white/20 transition-all"
                        >
                          <Copy size={12} />
                          Copy Data
                        </button>
                        <button 
                          onClick={() => {
                            const message = `Please help with this 3D object.\n\nNotes: ${annotationText}\n\nOBJECT INFO:\n\`\`\`json\n${infoString}\n\`\`\``;
                            onSendToChat(message);
                            onClose();
                          }}
                          className="flex-1 py-2 bg-[rgba(0,0,0,0.98)] border border-white/20 rounded-lg text-white text-[10px] font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 hover:border-white/20 transition-all"
                        >
                          <MessageSquare size={12} />
                          Send to Chat
                        </button>
                     </div>
                     
                     {/* AI Analysis Button */}
                     <div className="mt-3 pt-3 border-t border-white/20">
                       <button 
                         onClick={() => onAskAI(selectedObject, annotationText)}
                         disabled={isGenerating}
                         className={`w-full py-2.5 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all ${
                           isGenerating ? 'bg-[rgba(0,0,0,0.98)] text-white cursor-not-allowed' : 'bg-[rgba(0,0,0,0.98)] text-white hover:scale-[1.02]'
                         }`}
                       >
                         {isGenerating ? <><Loader2 size={12} className="animate-spin" /> Analyzing...</> : <><Sparkles size={12} /> Ask AI</>}
                       </button>
                     </div>
                </div>
            </div>

            {/* Footer */}
            <div className="p-4 pt-2 border-t border-white/20 bg-[rgba(0,0,0,0.98)] rounded-b-2xl">
                <div className="flex gap-2">
                    <input 
                        autoFocus 
                        type="text" 
                        value={annotationText} 
                        onChange={(e) => setAnnotationText(e.target.value)} 
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && annotationText.trim()) {
                                onAnnotate(annotationText, selectedObject);
                                setAnnotationText('');
                            }
                        }} 
                        placeholder="Task description..." 
                        className="flex-1 bg-[rgba(0,0,0,0.98)] border border-white/20 rounded-lg px-3 py-2 text-xs text-white outline-none" 
                    />
                    <button 
                      onClick={() => {
                          if (annotationText.trim()) {
                              onAnnotate(annotationText, selectedObject);
                              setAnnotationText('');
                          }
                      }}
                      disabled={!annotationText.trim()}
                      className="w-10 h-10 flex items-center justify-center bg-[rgba(0,0,0,0.98)] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Add to To-Do"
                    >
                      <Plus size={20} />
                    </button>
                </div>
            </div>
        </div>
    </div>
  );
}
